import type { VoidFunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { icons, TextInput } from '../../../../../generic';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../../../../generic/Button';
import * as ReportGenerationStore from '../../../../../../redux/reportGeneration';
import styles from '../SidePanel.module.less';
import { window } from '../../../../../../globals';
import type { ReportButtonBaseProps } from './types';

/**
 * Provides a button to queue an emailed report for the distribution panel
 */
const EmailButton: VoidFunctionComponent<ReportButtonBaseProps> = ({ reportContext, reportOptions }) => {
  const dispatch = useDispatch();
  const { isLoading } = useSelector(ReportGenerationStore.selectors.getQueueEmailReportStatus);
  const [hasQueuedReport, setHasQueuedReport] = useState<boolean>(false);

  // Reset queued when context shifts
  useEffect(() => {
    setHasQueuedReport(false);
  }, [setHasQueuedReport, reportContext, reportOptions]);

  const emailReport = async () => {
    dispatch(ReportGenerationStore.actions.queueEmailReport(reportContext, reportOptions));
    setHasQueuedReport(true);
  };

  return (
    <div className={cx(styles.emailButton, styles.actionButton)}>
      <TextInput
        size="small"
        label="Email"
        // Note: email is technically dynamic, but should rarely change during the lifetime of this component
        value={window.user?.email}
        title="The report will be sent to your configured email address."
        isDisabled
      />
      <Button
        type={BUTTON_TYPE.PRIMARY}
        size={BUTTON_SIZE.MEDIUM}
        icon={icons.fileOutline}
        onClick={emailReport}
        isDisabled={isLoading}
        isFullWidth
      >
        {!hasQueuedReport && !isLoading && 'Email Report'}
        {hasQueuedReport && isLoading && 'Queuing Report'}
        {hasQueuedReport && !isLoading && 'Queued Report'}
      </Button>
    </div>
  );
};

export default EmailButton;
